import { LoadingOutlined, ZoomInOutlined } from "@ant-design/icons";
import { Loader, Text } from "@mantine/core";
import { API, Storage } from "aws-amplify";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  ArrowIcon,
  CheckMarkIcon,
  DownloadIconAlt,
  UploadIconBlue,
  WarningIcon,
  XMarkIcon,
} from "../../assets/svgs";
import config from "../../aws-exports";
import {
  ApprovalStatus,
  ImageFile,
  PdfData,
  Position,
  PositionImage,
  UploadStatus,
  UserOrder,
} from "../../interfaces";
import {
  asyncForEach,
  calculateImageV2,
  createImgThumbnail,
  createPdfThumbnail,
  getOutline,
  getRatio,
  getRemainingColor,
  getRemainingCount,
  resetInset,
  updateProgress,
} from "../../utils";
import ImageDetail from "../account/ImageDetail";
import PdfPreview from "../account/PdfPreview";
import RenderBackgroundLoader from "../account/RenderBackgroundLoader";
import SendPrintdata from "../account/SendPrintdata";
import ImageGallery from "../image/ImageGallery";
import ImageOutline from "../image/ImageOutline";
import ImagePreviewLoading from "../ImagePreviewLoading";
import Modal from "../modal/Modal";
import DeclineAllModal from "./ViewOrder/DeclineAllModal";
import AcceptAllModal from "./ViewOrder/AcceptAllModal";
import useOrderPosition from "../../hooks/useOrderPosition";
import useOrder from "../../hooks/useOrder";

type Props = {
  item: Position;
  index: number;
  order: UserOrder;
  mutate: (state: UserOrder) => void;
};

interface RecentUrls {
  [key: string]: string;
}

var request: XMLHttpRequest | null = null;

const PositionItem: React.FC<Props> = ({ item, order, mutate, index }) => {
  const [recentUrls, setRecentUrls] = useState<RecentUrls>({});
  const [signedUrls, setSignedUrls] = useState<any>({});
  const [url, setUrl] = useState<string | null>(null);
  const { handleOrderUpdate } = useOrder();
  const [approveModalVisible, setApproveModalVisible] =
    useState<boolean>(false);
  const [declineModalVisible, setDeclineModalVisible] =
    useState<boolean>(false);
  const [base64, setBase64] = useState<string>();
  const [current, setCurrent] = useState<PositionImage | null>(null);
  const [open, setOpen] = useState(
    index === 0 ||
    (item?.positionImage && item.positionImage?.length > 0) ||
    false
  );
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState<boolean>(false);

  const [uploadStatus, setUploadStatus] = useState<UploadStatus | undefined>();
  const [approved, setApproved] = useState<boolean>(false);
  const [uploadMode, setUploadMode] = useState<boolean>(false);
  const [type, setType] = useState<"image" | "pdf" | null>(null);
  const [image, setImage] = useState<File | null>(null);
  const [outline, setOutline] = useState<string>();
  const [dpi, setDpi] = useState<number>();
  const [ratio, setRatio] = useState<{
    x: number;
    y: number;
    fit: boolean;
  }>();
  const { dontApproveImage, approveImage } = useOrderPosition(order);
  const [pdfKey, setPdfKey] = useState<string>();
  const [isLarge, setIsLarge] = useState<boolean>(false);
  const [galleryImage, setGalleryImage] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const inputRef: any = useRef();

  const galleryPreview = (imgUrl?: string) => {
    if (imgUrl) {
      setGalleryImage(imgUrl);
    } else {
      const preview = document.getElementById(
        `image-preview_${index}`
      ) as HTMLImageElement;
      if (preview) setGalleryImage(preview.src);
    }
  };

  const setSignedUrl = async (url: string) => {
    if (url) {
      const signedUrl = await Storage.get(url);
      setSignedUrls({ ...signedUrls, [url]: signedUrl });
    }
  };

  const loadPdf = async (key: string) => {
    if (recentUrls[key]) {
      setUrl(recentUrls[key]);
      onModalOpen();
      return;
    }

    const signedUrl = await Storage.get(key);
    setRecentUrls({ ...recentUrls, [key]: signedUrl });
    setUrl(signedUrl);
    onModalOpen();
  };

  const loadImage = async (key: string) => {
    if (recentUrls[key]) {
      setUrl(recentUrls[key]);
      onModalOpen();
      return;
    }

    const signedUrl = await Storage.get(key);
    setRecentUrls({ ...recentUrls, [key]: signedUrl });
    setUrl(signedUrl);
    onModalOpen();
  };

  const fetchImage = async () => {
    const urls: any = {};

    await asyncForEach(item.positionImage, async (position: PositionImage) => {
      const url: any = await Storage.get(position.thumbnail);
      urls[position.thumbnail] = url;
    });

    setSignedUrls(urls);
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setOutline(getOutline({ image: e.currentTarget, item }));
  };

  const handleDownload = async (key: string) => {
    const result: any = await Storage.get(key, { download: true });
    if (result instanceof Blob) {
      const url = URL.createObjectURL(result);
      triggerDownload(url, key);
    } else if ("Body" in result && result.Body instanceof Blob) {
      const url = URL.createObjectURL(result.Body);
      triggerDownload(url, key);
    }

  };

  const triggerDownload = (url: string, filename: string) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };


  const handleUpload = () => {
    if (item.White) return;
    setTimeout(() => inputRef.current.click(), 30);
  };

  const onDeclineModalOpen = () => {
    setDeclineModalVisible(true);
  };

  const onApproveModalOpen = () => {
    setApproveModalVisible(true);
  };

  useEffect(() => {
    item.positionImage && fetchImage();

  }, []);

  const onModalOpen = () => {
    setModalVisible(true);
  };

  const onModalClose = () => {
    if (request) {
      request.abort();
    }

    resetState();

    const preview = document.getElementById(
      `image-preview_${index}`
    ) as HTMLImageElement;

    setTimeout(() => {
      setImage(null);
      setType(null);
      if (preview) preview.src = "";
    }, 300);
  };

  const onComplete = async (updated: PositionImage) => {
    const positionImage: PositionImage[] = [...(item.positionImage || [])].map(
      (image) => {
        if (image.key === current?.key) {
          return updated;
        }
        return image;
      }
    );
    const positions = order.positions.map((position: Position) => {
      if (
        position.articleNumber === item.articleNumber &&
        position.heightByMm === item.heightByMm &&
        position.widthByMm === item.widthByMm
      ) {
        return { ...position, positionImage };
      }
      return {
        ...position,
        positionImage: position.positionImage ? position.positionImage : [],
      };
    });
    const updatedOrder = await handleOrderUpdate({ id: order.id, positions });
    if (updatedOrder) mutate(updatedOrder);
    setSignedUrl(updated.thumbnail);
    setCurrent(updated);
    setUploadMode(false);
    if (current) {
      Storage.remove(current.key);
      Storage.remove(current.thumbnail);
    }
    setUploadStatus(UploadStatus.DONE);
    setTimeout(() => onModalClose(), 300);
  };

  const handleS3Upload = async () => {
    if (!dpi || !ratio || !current || !image) return;

    if (isLarge && !pdfKey) {
      toast.info(t("uploadInProgress"));
      return;
    }

    if (numPages > 1) {
      toast.error(t("moreThan1Page"));
      return;
    }

    if (!ratio?.fit) {
      toast.error(t("imageDoesNotMatch"));
      return;
    }

    // Uploaded large PDF file key is present
    if (pdfKey) {
      setUploadStatus(UploadStatus.PREUPLOADED);

      const currentUpdated: PositionImage = {
        ...current,
        key: `${pdfKey}.jpg`,
        pdfKey,
        thumbnail: `${pdfKey}x300.jpg`,
        // dpi,
        // aspectRatio: ratio,
      };

      await onComplete(currentUpdated);
    } else {
      setUploadStatus(UploadStatus.UPLOADING);

      const fileName = `${Date.now()}-${image.name}`;
      const thumbnailFileName = `x300-${fileName}`;
      const thumbnail = await (type === "image"
        ? createImgThumbnail(index, image.name, image.type)
        : createPdfThumbnail(image.name, image.type));
      const res = await Storage.put(thumbnailFileName, thumbnail);

      const region = config.aws_user_files_s3_bucket_region;
      const bucketName = config.aws_user_files_s3_bucket;

      const signedUrl = await API.get(
        "s3UploadApi",
        `/?fileName=public/${fileName}&region=${region}&bucketName=${bucketName}`,
        {}
      );

      request = new XMLHttpRequest();
      request.responseType = "json";

      const line = document.getElementById("upload-line") as HTMLElement;
      const progress = document.getElementById(
        "upload-progress"
      ) as HTMLElement;

      request.upload.addEventListener("progress", (e: any) => {
        updateProgress({ line, progress, e });
      });

      request.addEventListener("load", async () => {
        if (request?.status === 200) {
          setUploadStatus(UploadStatus.DONE);

          const currentUpdated: PositionImage = {
            ...current,
            key: fileName,
            pdfKey,
            thumbnail: res.key,
            // dpi,
            // aspectRatio: ratio,
          };

          await onComplete(currentUpdated);
        } else {
          request = null;
        }
      });

      request.addEventListener("error", (e: any) => {
        request = null;
        console.log("error:", e);
      });

      request.open("put", signedUrl);
      request.send(image);
    }
  };

  const resetState = () => {
    setModalVisible(false);
    setApproved(false);
    setUploadStatus(undefined);
    setPdfKey(undefined);
    setIsLarge(false);
    setTimeout(() => {
      setCurrent(null);
      setUrl(null);
      setOutline(undefined);
      setNumPages(0);
      setDpi(undefined);
      setRatio(undefined);
      resetInset(index);
    }, 300);
  };

  const onDeclineImage = async (
    rejectedDescription: string,
    rejectedReason: string,
    files: ImageFile[]
  ) => {
    setDeclineModalVisible(false);
    const updatedOrder = await dontApproveImage(
      rejectedDescription,
      rejectedReason,
      files,
      current,
      item,
      order
    );
    if (updatedOrder) {
      mutate(updatedOrder);
    }
    toast.success(t("imageRejected"));
  };

  const onApproveImage = async (acceptedDescription: string) => {
    setApproveModalVisible(false);
    const updatedOrder = await approveImage(
      acceptedDescription,
      current,
      item,
      order
    );
    if (updatedOrder) {
      mutate(updatedOrder);
    }
    toast.success(t("imageApproved"));
  };
  const onInputChange = (e: any) => {
    if (!current) return;

    const file = e.target.files[0];
    e.target.value = null;

    if (!/png|jpeg|jpg|pdf|epub/.test(file?.type)) {
      toast.warn(t("unsupportedFileWithName", { name: file.name }));
      return;
    }

    setUploadMode(true);

    setUrl(null);
    setImage(null);
    setDpi(undefined);
    setRatio(undefined);
    setOutline(undefined);
    setNumPages(0);
    onModalOpen();

    if (/png|jpeg|jpg/.test(file.type)) {
      setType("image");
      const img = new Image();
      img.src = URL.createObjectURL(file);

      img.onload = () => {
        const width = img.naturalWidth;
        const height = img.naturalHeight;

        const { _ratio, _dpi } = calculateImageV2({ width, height, item });
        setRatio(_ratio);
        setDpi(_dpi);
        setImage(file);

        if (_ratio.fit) {
          const preview = document.getElementById(
            `image-preview_${index}`
          ) as HTMLImageElement;
          preview.src = img.src;
          setUrl(img.src);
        }
      };
    } else if (/pdf|epub/.test(file.type)) {
      setType("pdf");
      setBase64(URL.createObjectURL(file));
      setTimeout(() => setImage(file), 100);
    } else {
      toast.warn(t("unsupportedFile"));
    }
  };

  const setPdfData = (pdfData: PdfData) => {
    const { _ratio, _dpi, _key, _numPages, _isLarge } = pdfData;

    if (_numPages) setNumPages(_numPages);

    if (image) {
      if (_isLarge) setIsLarge(true);
      if (_key) setPdfKey(_key);
      if (_ratio) setRatio(_ratio);
      if (_dpi) setDpi(_dpi);
    }
  };

  return (
    <div className={`position-item ${open ? "opened" : ""}`}>
      {galleryImage && (
        <ImageGallery
          image={galleryImage}
          outline={outline}
          onClose={() => setGalleryImage(null)}
        />
      )}
      <div
        className={`position-item__header ${item.White ? "white" : ""}`}
        onClick={() => !item.White && setOpen(!open)}
      >
        <div className="header-item">
          <h2 className="text-uppercase">
            {index + 1}. {item.articleName}
          </h2>
          <h2 className="color-grey">{item.articleNumber}</h2>
        </div>
        <div className="header-item">
          <h2>
            {item.widthByMm} x {item.heightByMm}
          </h2>
        </div>
        <div className="header-item">
          <h2>{item.quantity}x</h2>
        </div>
        <div className="header-item">
          <h2 style={{ color: getRemainingColor(item) }}>
            {getRemainingCount(item)}
          </h2>
        </div>
        <div className="header-item">
          <ArrowIcon hide={item.White} />
        </div>
      </div>
      <div className="position-item__body">
        {[...(item.positionImage || [])].map((positionItem, idx) => {
          const {
            thumbnail,
            quantity,
            key,
            pdfKey,
            // dpi,
            // aspectRatio,
            approvalStatus,
          } = positionItem;

          return (
            <div
              key={idx}
              className="upload-item"
              style={{
                backgroundImage: `url(${signedUrls[thumbnail]})`,
                border:
                  approvalStatus === ApprovalStatus.Yes
                    ? "3px solid #34C759"
                    : approvalStatus === ApprovalStatus.No
                      ? "3px solid crimson"
                      : 0,
              }}
            >
              <RenderBackgroundLoader src={signedUrls[thumbnail]} />
              <div className="upload-item__header">
                <div className="item-quantity">
                  {t("quantity")} {quantity}
                </div>
              </div>
              <div className="upload-item__footer">
                <div
                  onClick={() => {
                    setCurrent(positionItem);
                    onApproveModalOpen();
                  }}
                  className="upload-item-icon"
                >
                  <CheckMarkIcon />
                </div>
                <div
                  onClick={() => {
                    setCurrent(positionItem);
                    onDeclineModalOpen();
                  }}
                  className="upload-item-icon"
                >
                  <XMarkIcon />
                </div>
                {/* <div
                  onClick={() => {
                    setCurrent(positionItem);
                    handleUpload();
                  }}
                  className="upload-item-icon"
                >
                  <UploadIconAlt />
                </div> */}
                {true ? (
                  <div
                    className="upload-item-icon"
                    style={{
                      background: "transparent",
                      cursor: "default",
                    }}
                  ></div>
                ) : (
                  <div
                    className="upload-item-icon"
                    onClick={() => {
                      if (!positionItem.pdfKey && /\.pdf|epub+$/i.test(key)) {
                        setType("pdf");
                        loadPdf(key);
                      } else {
                        setType("image");
                        loadImage(key);
                      }
                      setCurrent(positionItem);
                    }}
                  >
                    <WarningIcon />
                  </div>
                )}
                <div
                  onClick={() => handleDownload(pdfKey || key)}
                  className="upload-item-icon"
                >
                  <DownloadIconAlt />
                </div>
              </div>
            </div>
          );
        })}
        {item.positionImage?.length === 0 && <p>No images uploaded</p>}
        <input
          accept=".png, .jpeg, .jpg, .pdf, .epub"
          style={{ display: "none" }}
          ref={inputRef}
          type="file"
          onChange={onInputChange}
        />
      </div>

      <Modal compact visible={modalVisible} onClose={onModalClose}>
        {current && !image ? (
          <>
            <div className="modal-preview">
              <div className="image-wrapper">
                {outline && (
                  <div className="image-wrapper__info">
                    <div className="marker-line" />
                    <Text color="#fa00ff" weight={700}>
                      {t("cutting")}
                    </Text>
                  </div>
                )}
                <div className="image-container">
                  <ImageOutline outline={outline} />
                  <img
                    style={{
                      visibility: url ? "visible" : "hidden",
                      display: type === "image" && url ? "block" : "none",
                    }}
                    src={url || ""}
                    id={`image-preview_${index}`}
                    className="image-preview"
                    onLoad={onImageLoad}
                    alt=""
                    onClick={() => galleryPreview()}
                  />
                </div>
              </div>
              {type === "image" ? (
                <>{!url && <ImagePreviewLoading />}</>
              ) : type === "pdf" ? (
                <>
                  {url ? (
                    <PdfPreview
                      item={item}
                      url={url}
                      outline={outline}
                      setPdfData={setPdfData}
                      previewPdf={galleryPreview}
                      setOutline={setOutline}
                    />
                  ) : (
                    <Loader />
                  )}
                </>
              ) : (
                <LoadingOutlined style={{ fontSize: 28 }} />
              )}
              {type === "image" && url && (
                <>
                  <div
                    onClick={() => galleryPreview()}
                    className="image-action"
                  >
                    <span>{t("imageFullScreen")}</span>
                    <ZoomInOutlined style={{ fontSize: 28, marginLeft: 5 }} />
                  </div>
                  <span className="text-small">
                    {t("imagePreviewDescription")}
                  </span>
                </>
              )}
            </div>
            <div className="modal-info">
              <ImageDetail
                productRatio={getRatio(item.widthByMm, item.heightByMm)}
                {...(!uploadMode
                  ? {
                    numPages,
                  }
                  : {
                    dpi,
                    ratio,
                    numPages,
                  })}
              />
            </div>
          </>
        ) : (
          <>
            <div className="modal-preview">
              {/* Image */}
              <div className="image-wrapper">
                {outline && type === "image" && (
                  <div className="image-wrapper__info">
                    <div className="marker-line" />
                    <Text color="#fa00ff" weight={700}>
                      {t("cutting")}
                    </Text>
                  </div>
                )}
                <div className="image-container">
                  <ImageOutline outline={outline} />
                  <img
                    style={{
                      visibility: image ? "visible" : "hidden",
                      display:
                        type === "image" && ratio?.fit ? "block" : "none",
                      maxHeight: type === "image" && ratio?.fit ? "400px" : "0",
                    }}
                    id={`image-preview_${index}`}
                    className="image-preview"
                    onLoad={onImageLoad}
                    alt=""
                    onClick={() => galleryPreview()}
                  />
                </div>
              </div>
              {image ? (
                <>
                  {/* PDF */}
                  {type === "pdf" && (
                    <PdfPreview
                      item={item}
                      image={image}
                      base64={base64}
                      outline={outline}
                      url={base64 || URL.createObjectURL(image)}
                      setPdfData={setPdfData}
                      previewPdf={galleryPreview}
                      setOutline={setOutline}
                    />
                  )}
                  {ratio && !ratio?.fit && (
                    <div className="row row-center">
                      <span className="text-center" style={{ fontSize: 18 }}>
                        {t("imageDoesNotMatch")}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <>{type === "image" ? <ImagePreviewLoading /> : <Loader />}</>
              )}
              {type === "image" && url && ratio && ratio?.fit && (
                <>
                  <div
                    onClick={() => galleryPreview()}
                    className="image-action"
                  >
                    <span>{t("imageFullScreen")}</span>
                    <ZoomInOutlined style={{ fontSize: 28, marginLeft: 5 }} />
                  </div>
                  <span className="text-small">
                    {t("imagePreviewDescription")}
                  </span>
                </>
              )}
            </div>
            <div className="modal-info">
              <ImageDetail
                productRatio={getRatio(item.widthByMm, item.heightByMm)}
                {...(!uploadMode
                  ? {
                    numPages,
                  }
                  : {
                    dpi,
                    ratio,
                    numPages,
                  })}
              />
            </div>
            <div
              className="choose-another-file"
              style={{ pointerEvents: !!uploadStatus ? "none" : "unset" }}
            >
              <span onClick={handleUpload}>
                <span>{t("chooseAnotherFile")} </span>
                <UploadIconBlue />
              </span>
            </div>
            {ratio && ratio?.fit && (
              <SendPrintdata
                uploadStatus={uploadStatus}
                approved={approved}
                setApproved={setApproved}
                image={image}
                handleS3Upload={handleS3Upload}
                isLarge={isLarge}
              />
            )}
          </>
        )}
      </Modal>
      {approveModalVisible && (
        <AcceptAllModal
          modalVisible={approveModalVisible}
          setModalVisible={setApproveModalVisible}
          onSuccess={(acceptedDescription: string) =>
            onApproveImage(acceptedDescription)
          }
          successButtonName={"approve"}
          title={"approvePrintdata"}
          contentStyle={"pb-16"}
        />
      )}
      {declineModalVisible && (
        <DeclineAllModal
          current={current}
          modalVisible={declineModalVisible}
          setModalVisible={setDeclineModalVisible}
          onSuccess={(
            rejectedDescription: string,
            rejectedReason: string,
            files: ImageFile[]
          ) => onDeclineImage(rejectedDescription, rejectedReason, files)}
          successButtonName={"doNotApprove"}
          title={"doNotApprovePrintdata"}
          contentStyle={"pb-16"}
        />
      )}
    </div>
  );
};

export default PositionItem;
